import { CheckIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@/features/auth/hooks/use-user';
import { OrgMemberWithProfile } from '@/features/org/api/get-org-members';
import { OrgRole } from '@/features/org/validation/org-role.schema';
import ArrowDown from '@/shared/assets/chevron-down.svg?react';
import { Button } from '@/shared/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/shared/components/ui/command';
import { Label } from '@/shared/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { useToast } from '@/shared/hooks/use-toast';
import { cn } from '@/shared/utils/cn';

export const OrgMemberPicker = ({
  orgMembers,
  existingInvitations,
  onSuccess,
  onlyMemberRole,
}: {
  orgMembers: OrgMemberWithProfile[] | undefined;
  existingInvitations: string[];
  onSuccess: (memberId: string) => void;
  onlyMemberRole?: boolean;
}) => {
  const { t } = useTranslation('org', {
    keyPrefix: 'pick_org_member.org_member_picker',
  });
  const { toast } = useToast();
  const [selectOpen, setSelectOpen] = useState(false);

  const { data: user } = useUser();

  const handleSelectExistingMember = (member: OrgMemberWithProfile) => {
    if (member) {
      if (existingInvitations.some((inv) => inv === member.member_id)) {
        toast({
          title: t('errors.email_already_invited'),
          variant: 'destructive',
        });
        return;
      }

      if (member.user_id === user?.id) {
        toast({
          title: t('errors.cannot_invite_self'),
          variant: 'destructive',
        });
        return;
      }

      if (onlyMemberRole && member.role !== OrgRole.MEMBER) {
        toast({
          title: t('errors.only_member_role'),
          variant: 'destructive',
        });
        return;
      }

      onSuccess(member.member_id);
    }
  };

  return (
    <div className="flex w-full flex-col items-stretch gap-2">
      <Label variant="form">{t('existing_members')}</Label>
      <Popover open={selectOpen} onOpenChange={setSelectOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={selectOpen}
            className="w-full justify-between rounded-xs !text-custom-light-grey hover:bg-transparent"
          >
            {t('select_member_placeholder')}
            <ArrowDown
              className={cn(
                'h-[19px] w-[18px] font-medium text-white',
                selectOpen ? 'rotate-180' : '',
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popper-anchor-width)] rounded-xs p-0">
          <Command>
            <CommandInput placeholder={t('search_members')} />
            <CommandEmpty>{t('no_members_found')}</CommandEmpty>
            <CommandGroup>
              {orgMembers?.map((member) => (
                <CommandItem
                  className="text-black"
                  key={member.user_id}
                  value={member.user_id}
                  onSelect={() => {
                    handleSelectExistingMember(member);
                    setSelectOpen(false);
                  }}
                >
                  <CheckIcon className={cn('mr-2 h-4 w-4', 'opacity-0')} />
                  {member.profile?.name} ({member.profile?.email})
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
